import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText, Receipt, ArrowLeft, CheckCircle, XCircle, Clock,
  SpinnerGap, Warning, Shield, PaperPlaneTilt, Prohibit, X, DownloadSimple
} from '@phosphor-icons/react';
import { documentsAPI, userAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import SignaturePad from '../components/SignaturePad';
import { toast } from '../hooks/use-toast';

const STATUS_LABEL = {
  pending_signatures: { label: 'Awaiting signatures', cls: 'bg-amber-100 text-amber-800 border-amber-400', Icon: Clock },
  signed: { label: 'Fully signed', cls: 'bg-emerald-100 text-emerald-800 border-emerald-400', Icon: CheckCircle },
  declined: { label: 'Declined', cls: 'bg-rose-100 text-rose-800 border-rose-400', Icon: XCircle },
  cancelled: { label: 'Cancelled', cls: 'bg-neutral-200 text-neutral-700 border-neutral-400', Icon: Prohibit },
};

export default function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [signature, setSignature] = useState({ type: 'drawn', data: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    if (!doc) return;
    setDownloading(true);
    try {
      const res = await documentsAPI.downloadPdf(doc._id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const safe = (doc.title || 'document').replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '') || 'document';
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safe}_${doc._id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: 'Could not download PDF',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [docRes, meRes] = await Promise.all([
        documentsAPI.get(id),
        userAPI.getMe(),
      ]);
      setDoc(docRes.data.document);
      setMe(meRes.data?.user || meRes.data);
    } catch (err) {
      toast({ title: 'Could not load document', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
      navigate('/documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const mySigner = useMemo(() => {
    if (!doc || !me) return null;
    return (doc.signers || []).find(s => s.user_id === me._id);
  }, [doc, me]);

  const isCreator = doc && me && doc.creator_id === me._id;
  const canSign = mySigner && mySigner.status === 'pending' && doc.status === 'pending_signatures';
  const signedCount = (doc?.signers || []).filter(s => s.status === 'signed').length;
  const totalSigners = (doc?.signers || []).length;

  const handleSign = async () => {
    if (!agreed) {
      toast({ title: 'Please accept the e-signature terms', variant: 'destructive' });
      return;
    }
    if (!signature.data || (signature.type === 'typed' && signature.data.trim().length < 2)) {
      toast({ title: 'Add your signature', description: 'Draw or type your signature before submitting.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await documentsAPI.sign(id, { signature_type: signature.type, signature_data: signature.data });
      setDoc(res.data.document);
      toast({ title: 'Signed', description: 'Your signature has been recorded with a full audit trail.' });
      setSignature({ type: 'drawn', data: '' });
      setAgreed(false);
    } catch (err) {
      toast({ title: 'Signing failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    setSubmitting(true);
    try {
      await documentsAPI.decline(id, declineReason);
      toast({ title: 'Document declined', description: 'The other parties have been notified.' });
      setShowDecline(false);
      load();
    } catch (err) {
      toast({ title: 'Decline failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this document? Signers will be notified and it cannot be re-opened.')) return;
    try {
      await documentsAPI.cancel(id);
      toast({ title: 'Document cancelled' });
      load();
    } catch (err) {
      toast({ title: 'Cancel failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0F0F2]">
        <SpinnerGap size={48} weight="bold" className="text-[#2B4C6F] animate-spin" />
      </div>
    );
  }
  if (!doc) return null;

  const sty = STATUS_LABEL[doc.status] || STATUS_LABEL.pending_signatures;
  const StatusIcon = sty.Icon;

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-16" data-testid="document-detail">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={() => navigate('/documents')}
            className="flex items-center gap-1 text-blue-100 hover:text-white text-sm font-bold mb-2"
            data-testid="back-to-documents"
          >
            <ArrowLeft size={16} weight="bold" />
            Back to Documents
          </button>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {doc.document_type === 'change_order'
                  ? <Receipt size={20} weight="bold" />
                  : <FileText size={20} weight="bold" />
                }
                <span className="text-xs font-black uppercase tracking-widest text-blue-100">
                  {doc.document_type === 'change_order' ? 'Change order' : 'Contract'}
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-black">{doc.title}</h1>
              <p className="text-blue-100 text-sm mt-1">Created {new Date(doc.created_at).toLocaleString()}</p>
            </div>
            <Badge className={`${sty.cls} text-sm font-bold border-2 px-3 py-1.5`}>
              <StatusIcon size={14} weight="bold" className="mr-1.5" />
              {sty.label}
            </Badge>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              type="button"
              onClick={handleDownloadPdf}
              disabled={downloading}
              className="bg-white text-[#0F172A] hover:bg-neutral-100 border-2 border-black font-bold shadow-[3px_3px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
              data-testid="document-download-pdf-btn"
            >
              {downloading ? (
                <SpinnerGap size={18} weight="bold" className="animate-spin mr-2" />
              ) : (
                <DownloadSimple size={18} weight="bold" className="mr-2" />
              )}
              {downloading ? 'Preparing PDF…' : 'Download PDF'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Progress bar */}
        <div className="bg-white border-2 border-black rounded-xl p-4 shadow-[3px_3px_0px_#000]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-black uppercase tracking-widest text-neutral-500">Signatures collected</span>
            <span className="font-mono font-black">{signedCount}/{totalSigners}</span>
          </div>
          <div className="h-3 bg-neutral-100 border-2 border-black rounded-sm overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${totalSigners ? (signedCount / totalSigners) * 100 : 0}%` }}
              data-testid="signatures-progress"
            />
          </div>
        </div>

        {/* Document body */}
        <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[3px_3px_0px_#000]" data-testid="document-body">
          <h2 className="font-black uppercase tracking-widest text-xs text-neutral-500 mb-3">Document content</h2>
          <pre className="whitespace-pre-wrap text-sm text-neutral-800 font-sans leading-relaxed">{doc.body}</pre>
          {doc.metadata && Object.keys(doc.metadata).length > 0 && (
            <div className="mt-4 pt-4 border-t border-neutral-200">
              <p className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">Details</p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                {Object.entries(doc.metadata).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 py-1 border-b border-dashed border-neutral-200">
                    <dt className="text-neutral-500 capitalize">{k.replace(/_/g, ' ')}</dt>
                    <dd className="font-medium text-neutral-900 text-right">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>

        {/* Signers list */}
        <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[3px_3px_0px_#000]">
          <h2 className="font-black uppercase tracking-widest text-xs text-neutral-500 mb-3">Signers</h2>
          <ul className="space-y-3" data-testid="signers-list">
            {(doc.signers || []).map((s) => (
              <li key={s.user_id} className="flex items-start justify-between gap-3 p-3 bg-neutral-50 border border-neutral-200 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-neutral-900 truncate">{s.name}</p>
                  <p className="text-xs text-neutral-500">{s.email} · <span className="capitalize">{s.role}</span></p>
                  {s.status === 'signed' && s.signed_at && (
                    <p className="text-[10px] text-neutral-400 mt-1 font-mono">
                      Signed {new Date(s.signed_at).toLocaleString()}
                      {s.ip_address ? ` · IP ${s.ip_address}` : ''}
                    </p>
                  )}
                  {s.status === 'signed' && s.signature_data && (
                    <div className="mt-2 inline-block bg-white border border-neutral-300 rounded p-1">
                      {s.signature_type === 'drawn' ? (
                        <img
                          src={s.signature_data}
                          alt={`Signature of ${s.name}`}
                          className="h-12"
                          data-testid={`signature-img-${s.user_id}`}
                        />
                      ) : (
                        <span className="text-xl px-2 inline-block" style={{ fontFamily: '"Dancing Script", "Brush Script MT", cursive' }}>
                          {s.signature_data}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <Badge className={
                  s.status === 'signed' ? 'bg-emerald-100 text-emerald-800 border-emerald-400 border-2 text-[10px] font-bold' :
                  s.status === 'declined' ? 'bg-rose-100 text-rose-800 border-rose-400 border-2 text-[10px] font-bold' :
                  'bg-amber-100 text-amber-800 border-amber-400 border-2 text-[10px] font-bold'
                }>
                  {s.status}
                </Badge>
              </li>
            ))}
          </ul>
        </div>

        {/* Sign panel */}
        {canSign && (
          <div className="bg-white border-2 border-emerald-500 rounded-xl p-6 shadow-[3px_3px_0px_#000]" data-testid="sign-panel">
            <div className="flex items-center gap-2 mb-3">
              <Shield size={20} weight="fill" className="text-emerald-600" />
              <h2 className="font-black uppercase tracking-widest text-xs text-emerald-700">Sign this document</h2>
            </div>
            <p className="text-sm text-neutral-600 mb-4">
              Review the content above carefully. Once signed, this document is legally binding and your IP, timestamp, and user-agent will be recorded as part of the audit trail.
            </p>

            <SignaturePad
              defaultName={me?.name || ''}
              onChange={setSignature}
            />

            <label className="flex items-start gap-2 mt-4 cursor-pointer" data-testid="agree-checkbox-label">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 w-4 h-4 cursor-pointer"
                data-testid="agree-checkbox"
              />
              <span className="text-xs text-neutral-700">
                I agree that my electronic signature is the legal equivalent of my handwritten signature, and that I have read and accept the terms of this document.
              </span>
            </label>

            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <Button
                onClick={handleSign}
                disabled={submitting || !agreed || !signature.data}
                className="flex-1 h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-black uppercase tracking-wider border-2 border-black rounded-lg shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-0.5 active:shadow-none"
                data-testid="sign-submit"
              >
                {submitting ? (
                  <SpinnerGap size={20} weight="bold" className="mr-2 animate-spin" />
                ) : (
                  <PaperPlaneTilt size={18} weight="fill" className="mr-2" />
                )}
                Sign & Submit
              </Button>
              <Button
                onClick={() => setShowDecline(true)}
                disabled={submitting}
                variant="outline"
                className="h-12 border-2 border-black font-bold"
                data-testid="decline-btn"
              >
                <X size={16} weight="bold" className="mr-2" />
                Decline
              </Button>
            </div>
          </div>
        )}

        {/* Already-signed acknowledgement */}
        {mySigner && mySigner.status === 'signed' && (
          <div className="bg-emerald-50 border-2 border-emerald-500 rounded-xl p-4 flex items-start gap-3" data-testid="already-signed-banner">
            <CheckCircle size={24} weight="fill" className="text-emerald-600 flex-shrink-0" />
            <div>
              <p className="font-bold text-emerald-800">You signed this document on {new Date(mySigner.signed_at).toLocaleString()}</p>
              <p className="text-sm text-emerald-700 mt-1">
                {doc.status === 'signed'
                  ? 'All parties have signed. The document is final.'
                  : 'Waiting on the remaining signers to complete.'}
              </p>
            </div>
          </div>
        )}

        {/* Creator cancel control */}
        {isCreator && doc.status === 'pending_signatures' && (
          <div className="bg-white border-2 border-rose-300 rounded-xl p-4 shadow-[3px_3px_0px_#000]">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-bold text-neutral-900 flex items-center gap-2">
                  <Warning size={18} weight="bold" className="text-rose-500" />
                  Creator controls
                </p>
                <p className="text-sm text-neutral-500">You drafted this document. You may cancel it if signing hasn&apos;t completed.</p>
              </div>
              <Button
                onClick={handleCancel}
                variant="outline"
                className="border-2 border-rose-400 text-rose-700 hover:bg-rose-50 font-bold"
                data-testid="cancel-doc-btn"
              >
                <Prohibit size={16} weight="bold" className="mr-2" />
                Cancel Document
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Decline modal */}
      <Dialog open={showDecline} onOpenChange={setShowDecline}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline this document?</DialogTitle>
            <DialogDescription>
              The other parties will be notified. You can provide an optional reason so the creator knows what to revise.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason for declining (optional)"
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            rows={4}
            data-testid="decline-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecline(false)}>Cancel</Button>
            <Button
              onClick={handleDecline}
              disabled={submitting}
              className="bg-rose-500 hover:bg-rose-600 text-white border-2 border-black font-bold"
              data-testid="decline-confirm"
            >
              {submitting ? <SpinnerGap size={18} weight="bold" className="mr-2 animate-spin" /> : <X size={16} weight="bold" className="mr-2" />}
              Decline Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
